import {
  PlayerName,
  PlayerProfile,
  PlayerSubheader
} from '@library/components/player';
import { DateTime } from 'luxon';
import type { FormattedPromotion } from '@library/types/promotions';
import { formatWithTimezoneChange } from '@library/utils/dateTime';

interface PromotionRowProps {
  promotion: FormattedPromotion;
}

export const PromotionRow = ({ promotion }: PromotionRowProps) => {
  const {
    id,
    attributes: {
      name,
      type,
      headline,
      useBy,
      createdAt,
      createdBy,
      approvedAt,
      approvedBy,
      macroPromoTag,
      microPromoTag
    }
  } = promotion;

  const formattedUseBy = formatWithTimezoneChange(
    DateTime.fromISO(useBy),
    'MMM d, h:mma'
  );

  const formattedCreatedAt = formatWithTimezoneChange(
    DateTime.fromISO(createdAt),
    'MMM d, h:mma'
  );

  const formattedApprovedAt = approvedAt
    ? formatWithTimezoneChange(DateTime.fromISO(approvedAt), 'MMM d, h:mma')
    : undefined;

  /**
   * A promotion is Pending approval if it has no approvedAt date and the use_by date is in the future
   * It's approved if it has an approvedAt date
   * It's expired if the use_by date is in the past and it has no approvedAt date
   */
  const getApprovalStatus = () => {
    if (approvedAt) {
      return 'Approved';
    }

    const useByDate = DateTime.fromISO(useBy);
    const isInFuture = useByDate > DateTime.now();

    return isInFuture ? 'Pending' : 'Expired';
  };

  const approvalStatus = getApprovalStatus();

  return (
    <div
      id="promotionPendingApprovalRowContainer"
      className="border-soFresh-140 text-soClean-110 hover:bg-soFresh-120 group/user text-2xs relative flex items-center border-b xl:text-xs"
      key={id}
    >
      <PlayerProfile
        className="flex w-[10%] items-center overflow-hidden text-ellipsis p-3"
        name={id}
        subheader={type}
      >
        <div id="promoNameAndShortDescContainer" className="flex flex-col">
          {/* Promotion ID */}
          <PlayerName className="font-heebo text-soClean-100 block text-sm font-medium capitalize tracking-tight" />
          {/* Promotion Type */}
          <PlayerSubheader className="text-soClean-170 font-heebo box-content line-clamp-1 overflow-hidden text-ellipsis text-xs font-medium tracking-tight" />
        </div>
      </PlayerProfile>

      <PlayerProfile
        className="flex w-[25%] items-center p-3"
        name={name}
        subheader={headline}
      >
        <div id="promoNameAndShortDescContainer" className="flex flex-col">
          {/* Promotion Name */}
          <PlayerName className="font-heebo text-soClean-100 block text-sm font-medium capitalize tracking-tight" />
          {/* Headline */}
          <PlayerSubheader className="text-soClean-170 font-heebo box-content line-clamp-1 overflow-hidden text-ellipsis text-xs font-medium tracking-tight" />
        </div>
      </PlayerProfile>

      {/* Use By Date */}
      <div className="flex w-[15%] items-center p-3">{formattedUseBy}</div>

      <PlayerProfile
        className="flex w-[15%] items-center p-3"
        name={formattedCreatedAt}
        subheader={createdBy}
      >
        <div className="flex flex-col">
          {/* Created At */}
          <PlayerName className="font-heebo text-soClean-100 block text-sm font-medium tracking-tight" />
          {/* Created By */}
          <PlayerSubheader className="text-soClean-170 font-heebo box-content line-clamp-1 overflow-hidden text-ellipsis text-xs font-medium tracking-tight" />
        </div>
      </PlayerProfile>

      {/* Macro Tag  */}
      <div className="flex w-[12.5%] items-center p-3">{macroPromoTag}</div>

      {/* Micro Tag  */}
      <div className="flex w-[12.5%] items-center p-3">{microPromoTag}</div>

      {/* Approval */}
      <PlayerProfile
        className="flex w-[10%] items-center p-3"
        name={
          approvalStatus === 'Approved' ? formattedApprovedAt! : approvalStatus
        }
        subheader={approvalStatus === 'Approved' ? approvedBy : ''}
      >
        <div className="flex flex-col">
          {/* Approval Status */}
          <PlayerName className="font-heebo text-soClean-100 block text-sm font-medium tracking-tight" />
          {/* Approved By */}
          <PlayerSubheader className="text-soClean-170 font-heebo box-content line-clamp-1 overflow-hidden text-ellipsis text-xs font-medium tracking-tight" />
        </div>
      </PlayerProfile>
    </div>
  );
};
